import React, { useEffect, useState } from 'react';
import { DatePicker, Select, Card, Button } from 'antd';
import { SearchOutlined, LoadingOutlined, DownloadOutlined } from '@ant-design/icons';
import dayjs, { type Dayjs } from 'dayjs';
import _ from 'lodash';
import {
  type FinancesFilters,
  type GroupByOption,
  type FormatOption,
  type SelectOption,
} from '../../../../types/finances';
import { createApiGateway } from '../../../../apiGateway';
import { useExtraCols } from '../../../../hooks/useExtraCols';
import { useIsMobile } from '../../../../hooks/useIsMobile';
import { buildExtraColsGroups } from '../utils/constants';

const apiGateway = createApiGateway();

const shortcutHint = ' (⌘/Ctrl+Enter)';

type BidderOption = {
  id: number;
  name: string;
};

interface FinancesFiltersProps {
  filters: FinancesFilters;
  onFiltersChange: (filters: Partial<FinancesFilters>) => void;
  companies: SelectOption<number>[];
  onApplyFilters: () => void;
  loading: boolean;
  hasUnappliedChanges: boolean;
  onExportCsv?: () => void;
  exportDisabled?: boolean;
}

const groupByOptions: GroupByOption[] = [
  { label: 'SSP', value: 'sspId' },
  { label: 'DSP', value: 'dspId' },
  { label: 'Company', value: 'companyId' },
  { label: 'Country', value: 'country' },
  { label: 'OS', value: 'os' },
  { label: 'Size', value: 'size' },
  { label: 'Publisher ID', value: 'pubId' },
  { label: 'Domain Bundle', value: 'domainBundle' },
];

const formatOptions: FormatOption[] = [
  { label: 'Summary', value: 'summary' },
  { label: 'By Day', value: 'by_day' },
  { label: 'By Hour', value: 'by_hour' },
];

export const FinancesFiltersComponent: React.FC<FinancesFiltersProps> = ({
  filters,
  onFiltersChange,
  companies,
  onApplyFilters,
  loading,
  hasUnappliedChanges,
  onExportCsv,
  exportDisabled,
}) => {
  const isMobile = useIsMobile();
  const [ssps, setSsps] = useState<BidderOption[]>([]);
  const [dsps, setDsps] = useState<BidderOption[]>([]);
  const { extraCols: availableExtraCols } = useExtraCols();
  const extraColsGroups = buildExtraColsGroups(availableExtraCols);

  useEffect(() => {
    (async () => {
      const [sspsResponse, dspsResponse] = await Promise.all([
        apiGateway.ssps.getAll({ includeSpend: false }),
        apiGateway.dsps.getAll({ includeSpend: false }),
      ]);

      setSsps(sspsResponse);
      setDsps(dspsResponse);
    })();
  }, []);

  const handleDateRangeChange = (dates: [Dayjs | null, Dayjs | null] | null) => {
    if (dates && dates[0] && dates[1]) {
      onFiltersChange({
        dateRange: [dates[0].format('YYYY-MM-DD'), dates[1].format('YYYY-MM-DD')],
      });
    }
  };

  const handleSelectChange =
    (field: keyof FinancesFilters) => (value: string | number | (string | number)[] | null) => {
      let processedValue: string | number | (string | number)[] | null = value;

      // Handle groupBy as array
      if (field === 'groupBy') {
        processedValue = Array.isArray(value) && value.length > 0 ? value : null;
      } else if (field === 'extraCols') {
        // Support "Select All" option for Extra Columns
        const ALL_VALUE = '__ALL__';
        if (Array.isArray(value) && value.includes(ALL_VALUE)) {
          processedValue = Array.from(availableExtraCols);
        } else {
          processedValue = Array.isArray(value) && value.length > 0 ? value : null;
        }
      } else {
        processedValue = value || null;
      }

      const newFilters: Partial<FinancesFilters> = {
        [field]: processedValue,
      };

      // Reset conflicting filters
      if (field === 'sspId') {
        newFilters.dspId = null;
      }
      if (field === 'dspId') {
        newFilters.sspId = null;
      }

      onFiltersChange(newFilters);
    };

  const sspOptions = _.sortBy(
    (ssps || []).map((ssp) => ({
      label: `[${ssp.id}] ${ssp.name}`,
      value: ssp.id,
    })),
    'value'
  );

  const dspOptions = _.sortBy(
    (dsps || []).map((dsp) => ({
      label: `[${dsp.id}] ${dsp.name}`,
      value: dsp.id,
    })),
    'value'
  );

  const companyOptions = companies;

  // Global hotkey: Cmd/Ctrl + Enter to apply filters
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
        // Avoid duplicate fetches while loading
        if (!loading) {
          e.preventDefault();
          onApplyFilters();
        }
      }
    };

    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onApplyFilters, loading]);

  return (
    <Card
      size="small"
      style={{
        marginBottom: '12px',
        borderRadius: '6px',
      }}
      styles={{ body: { padding: '12px' } }}
    >
      <div
        className="xchange-filters"
        style={{
          display: 'flex',
          gap: '8px',
          flexWrap: 'wrap',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        {/* Filter Controls */}
        <div
          style={{
            display: 'flex',
            gap: '8px',
            flexWrap: 'wrap',
            flex: '1',
            minWidth: '0', // Allow flex items to shrink below content size
          }}
        >
          <DatePicker.RangePicker
            value={[dayjs(filters.dateRange[0]), dayjs(filters.dateRange[1])]}
            onChange={handleDateRangeChange}
            size="small"
            style={{ minWidth: '200px', flex: '0 0 auto' }}
          />

          <Select
            options={formatOptions}
            value={filters.format}
            onChange={handleSelectChange('format')}
            placeholder="Format"
            size="small"
            style={{ minWidth: '100px', width: '120px' }}
          />

          <Select
            mode="multiple"
            options={groupByOptions}
            value={filters.groupBy}
            onChange={handleSelectChange('groupBy')}
            placeholder="Group By"
            size="small"
            style={{ minWidth: '120px', width: '160px' }}
            allowClear
            maxTagCount="responsive"
          />

          <Select
            options={sspOptions}
            mode="multiple"
            value={filters.sspId as unknown as number[] | null}
            onChange={handleSelectChange('sspId')}
            placeholder="SSP"
            size="small"
            style={{ minWidth: '120px', width: '160px' }}
            allowClear
            showSearch
            filterOption={(input, option) =>
              ((option?.label as string) || '').toLowerCase().includes(input.toLowerCase())
            }
          />

          <Select
            options={dspOptions}
            mode="multiple"
            value={filters.dspId as unknown as number[] | null}
            onChange={handleSelectChange('dspId')}
            placeholder="DSP"
            size="small"
            style={{ minWidth: '120px', width: '160px' }}
            allowClear
            showSearch
            filterOption={(input, option) =>
              ((option?.label as string) || '').toLowerCase().includes(input.toLowerCase())
            }
          />

          <Select
            options={companyOptions}
            mode="multiple"
            value={filters.companyId as unknown as number[] | null}
            onChange={handleSelectChange('companyId')}
            placeholder="Company"
            size="small"
            style={{ minWidth: '120px', width: '160px' }}
            allowClear
            showSearch
            filterOption={(input, option) =>
              ((option?.label as string) || '').toLowerCase().includes(input.toLowerCase())
            }
          />

          <Select
            options={[{ label: 'Select All', value: '__ALL__' }, ...extraColsGroups]}
            mode="multiple"
            value={filters.extraCols as string[] | null}
            onChange={handleSelectChange('extraCols')}
            placeholder="Extra Columns"
            size="small"
            style={{ minWidth: '220px', width: '260px' }}
            allowClear
            showSearch
            maxTagCount="responsive"
            filterOption={(input, option) =>
              ((option?.label as string) || '').toLowerCase().includes(input.toLowerCase())
            }
          />
        </div>

        {/* Export + Fetch Buttons */}
        <div style={{ flexShrink: 0, marginLeft: '8px', display: 'flex', gap: '8px' }}>
          <Button
            size="small"
            icon={<DownloadOutlined />}
            onClick={onExportCsv}
            disabled={loading || exportDisabled}
            title="Export current view to CSV"
            style={{ minWidth: '110px' }}
          >
            Export CSV
          </Button>
          <Button
            type="primary"
            size="small"
            icon={loading ? <LoadingOutlined /> : <SearchOutlined />}
            onClick={onApplyFilters}
            loading={loading}
            title={'Fetch (⌘/Ctrl + Enter)'}
            style={{
              ...(hasUnappliedChanges && {
                backgroundColor: '#1890ff',
                borderColor: '#1890ff',
              }),
              minWidth: '120px',
            }}
          >
            {loading
              ? 'Fetching...'
              : `${hasUnappliedChanges ? 'Fetch Statistics' : 'Up to Date'}${isMobile ? '' : shortcutHint}`}
          </Button>
        </div>
      </div>
    </Card>
  );
};
